import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Recycle } from "@phosphor-icons/react";

const LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_pos-service-system/artifacts/xtczyvft_Logo2.png";

const IS_PREVIEW = typeof window !== "undefined" && window.location.hostname.includes("preview");
const QUICK_ACCOUNTS = [
  { label: "Admin", email: "isalgroup1@gmail.com", password: "Regeneration2026!" },
  { label: "Dipendente", email: "dipendente@regeneration.store", password: "Dipendente2026!" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const doForgot = async () => {
    if (!forgotEmail) return toast.error("Inserisci la tua email");
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("Se l'email è registrata, riceverai un link per reimpostare la password");
      setShowForgot(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setForgotLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0F172A] p-12 text-white relative overflow-hidden">
        <div className="flex items-center gap-2">
          <Recycle size={28} weight="fill" className="text-[#84cc16]" />
          <span className="font-heading font-bold text-lg">UltraDigitale</span>
        </div>
        <div className="flex-1 flex items-center">
          <h1 className="font-heading text-5xl font-extrabold leading-tight">
            Gestionale<br />Magazzino &<br /><span className="text-[#3b82f6]">Riparazioni</span>
          </h1>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="text-center">
            <img src={LOGO} alt="logo" className="h-12 mx-auto object-contain mb-4" />
            <h2 className="font-heading text-2xl font-bold">Accedi al gestionale</h2>
            <p className="text-sm text-muted-foreground mt-1">Inserisci le tue credenziali</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email" placeholder="nome@esempio.com" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password" placeholder="••••••••" className="mt-1.5" />
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={() => { setForgotEmail(email); setShowForgot(true); }} className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Password dimenticata?</button>
            </div>
          </div>
          {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit">
            {loading ? "Accesso…" : "Accedi"}
          </Button>
          {IS_PREVIEW && (
            <div className="pt-3 border-t border-border" data-testid="quick-login">
              <p className="text-xs text-muted-foreground text-center mb-2">Accesso rapido (solo anteprima)</p>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_ACCOUNTS.map((c) => (
                  <Button key={c.label} type="button" variant="outline" disabled={loading}
                    onClick={async () => { setError(""); setLoading(true); try { await login(c.email, c.password); navigate("/"); } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); } finally { setLoading(false); } }}
                    data-testid={`quick-login-${c.label.toLowerCase()}`}>{c.label}</Button>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>

      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent data-testid="forgot-modal">
          <DialogHeader><DialogTitle>Password dimenticata</DialogTitle>
            <DialogDescription>Inserisci la tua email: se è registrata riceverai un link per reimpostare la password.</DialogDescription></DialogHeader>
          <div>
            <Label htmlFor="forgot-email">Email</Label>
            <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
              placeholder="nome@esempio.com" className="mt-1.5" data-testid="forgot-email" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForgot(false)}>Annulla</Button>
            <Button onClick={doForgot} disabled={forgotLoading} data-testid="forgot-submit">{forgotLoading ? "Invio…" : "Invia link"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
